import clsx from "clsx";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import UseAnimations from "react-useanimations";
import menu2 from "react-useanimations/lib/menu2";
import * as Yup from "yup";
import { ReadQuestionSectionByIdData, UpdateQuestionSectionData } from "../API/Question_Section_APIs";
import { lookupKeys } from "../../../lib/queries/lookups";

const validationSchema = Yup.object().shape({
  sectionName: Yup.string().required("Section name is required"),
  sectionDescription: Yup.string().required("Section description is required"),
  toBeLinkedWith: Yup.string(),
});

const QuestionSectionEditPage = (props?: {
  setPageLoading?: any;
}) => {
  const [loading, setLoading] = useState(false);
  const [questionSectionData, setQuestionSectionData] = useState<any>({
    sectionName: "",
    sectionDescription: "",
    toBeLinkedWith: "",
    sectionId: "",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // Fetch question section data when component mounts
  useEffect(() => {
    const fetchQuestionSection = async () => {
      if (id) {
        try {
          setLoading(true);
          const response = await ReadQuestionSectionByIdData(id);
          setQuestionSectionData(response.data);
        } catch (error) {
          console.error("Error fetching question section:", error);
          // Try to get data from location state as fallback
          const locationData = (location.state as any)?.data;
          if (locationData) {
            setQuestionSectionData(locationData);
          }
        } finally {
          setLoading(false);
        }
      } else {
        // Fallback to location state if no ID in URL
        const locationData = (location.state as any)?.data;
        if (locationData) {
          setQuestionSectionData(locationData);
        }
      }
    };

    fetchQuestionSection();
  }, [id, location.state]);

  const initialValues: any = {
    sectionId: questionSectionData.sectionId || id,
    sectionName: questionSectionData.sectionName || "",
    sectionDescription: questionSectionData.sectionDescription || "",
    toBeLinkedWith: questionSectionData.toBeLinkedWith || "",
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // Ensure sectionId is properly set
        const updateData = {
          ...values,
          sectionId: values.sectionId || id
        };
        await UpdateQuestionSectionData(updateData);
        queryClient.invalidateQueries(lookupKeys.questionSections);
        navigate("/question-sections"); // Navigate to question sections list
        if (props?.setPageLoading) {
          props.setPageLoading(["true"]);
        }
      } catch (error) {
        console.error("Error updating question section:", error);
        window.location.replace("/error");
      } finally {
        setLoading(false);
      }
    },
  });

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading question section...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Edit Question's Section</h1>
        <button
          className="btn btn-sm btn-icon btn-active-color-primary"
          onClick={() => navigate(-1)}
          aria-label="Close"
        >
          <UseAnimations
            animation={menu2}
            size={28}
            strokeColor={"#181C32"}
            reverse={true}
          />
        </button>
      </div>
      <form
        className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
        onSubmit={formik.handleSubmit}
      >
        <div className="mb-7">
          <label className="required fs-6 fw-bold mb-2">
            Section Name:
          </label>
          <input
            placeholder="Enter Section Name"
            {...formik.getFieldProps("sectionName")}
            className={clsx(
              "form-control form-control-lg form-control-solid",
              {
                "is-invalid text-danger":
                  formik.touched.sectionName &&
                  formik.errors.sectionName,
              },
              {
                "is-valid":
                  formik.touched.sectionName &&
                  !formik.errors.sectionName,
              }
            )}
          />
          {formik.touched.sectionName && formik.errors.sectionName && (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block text-danger">
                <span role="alert">{String(formik.errors.sectionName)}</span>
              </div>
            </div>
          )}
        </div>
        <div className="mb-7">
          <label className="required fs-6 fw-bold mb-2">
            Section Description:
          </label>
          <textarea
            placeholder="Enter Section Description"
            rows={4}
            {...formik.getFieldProps("sectionDescription")}
            className={clsx(
              "form-control form-control-lg form-control-solid",
              {
                "is-invalid text-danger":
                  formik.touched.sectionDescription &&
                  formik.errors.sectionDescription,
              },
              {
                "is-valid":
                  formik.touched.sectionDescription &&
                  !formik.errors.sectionDescription,
              }
            )}
          >
          </textarea>
          {formik.touched.sectionDescription && formik.errors.sectionDescription && (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block text-danger">
                <span role="alert">{String(formik.errors.sectionDescription)}</span>
              </div>
            </div>
          )}
        </div>
        <div className="mb-7">
          <label className="fs-6 fw-bold mb-2">
            To Be Linked With:
          </label>
          <textarea
            placeholder="e.g. another section name, assessment, tool, or any related context"
            rows={3}
            {...formik.getFieldProps("toBeLinkedWith")}
            className={clsx(
              "form-control form-control-lg form-control-solid",
              {
                "is-invalid text-danger":
                  formik.touched.toBeLinkedWith &&
                  formik.errors.toBeLinkedWith,
              },
              {
                "is-valid":
                  formik.touched.toBeLinkedWith &&
                  !formik.errors.toBeLinkedWith &&
                  !!formik.values.toBeLinkedWith,
              }
            )}
          >
          </textarea>
          <div
            className="form-text"
            style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 4 }}
          >
            Optional — note any section, tool, or content this section should connect to.
          </div>
          {formik.touched.toBeLinkedWith && formik.errors.toBeLinkedWith && (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block text-danger">
                <span role="alert">{String(formik.errors.toBeLinkedWith)}</span>
              </div>
            </div>
          )}
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-sm btn-light btn-active-light-secoundary"
            type="button"
            onClick={() => navigate(-1)}
          >
            Close
          </button>
          <button
            type="submit"
            id="kt_sign_up_submit"
            className="btn btn-sm btn-primary btn-active-light-secoundary"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className="indicator-label">Submit</span>}
            {loading && (
              <span className="indicator-progress" style={{ display: "block" }}>
                Please wait...{" "}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestionSectionEditPage;